import React, { useCallback } from 'react'
import { InfoContent, InfoLabel, InfoRow } from '@/components/InfoRow'
import Resx from '@/components/Resx'
import { formatDate } from '@/utils/dateUtilities'
import { ButtonGroup, Table } from 'reactstrap'
import ChangeItem from './changeItem'
import { Link } from 'react-router-dom'
import Icon from '@/components/Icon'
import { RichTextEditor } from '@/components/richTextEditor'
import { openDialog } from '@/store/Dialogs'
import { DatePicker } from '@/components/DatePicker'
import { EmployeeAssignmentDropDown } from '@/components/employeeAssignmentDropDown'
import { Validator } from '@/components/validation'
import { DropDownList } from '@/components/dropDownList'
import context from '@/context'
import { ApprovalResult } from './approvalResult'
import moment from 'moment'
import { useLocalization } from '@/hooks/localization'
import { toIntOrNull } from '@/utils/numberUtilities'
import { useSelector } from 'react-redux'
import Name from '@/components/Name'
import { ComposeMessageResultData } from '@/internal-app/pages/tasks/composeMessageResultData'
import { useDispatch } from '@/hooks/dispatch'
import { dialogTypes } from '@/dialogs'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { isNullOrUndefined } from '@/utils/isNullOrUndefined'
import { Button } from '@/components/button'

function TaskResultData ({ task, showDetail = true, checkerUserId }) {
  switch (task.data.resultType) {
    case 1: // ChangeRequest
      return <ChangeRequestTaskResult changeRequest={task.changeRequest} checkerUserId={checkerUserId} task={task} />
    case 2: // HtmlText
      return <HtmlTextTaskResult data={task.result} />
    case 3: // EmployeeAssignment
      return <EmployeeAssignmentTaskResult data={task.result} />
    case 4: // Message
      return <MessageTaskResult data={task.result} targetInstitution={toIntOrNull(task.data.data)} showDetail={showDetail} />
    case 5: // OutputReport
      return <OutputReportTaskResult data={task.result} />
    case 6: // Approval
      return <ApprovalTaskResult data={task.result} />
    default: // ButtonsOnly
      return <ButtonsResult task={task} />
  }
}

function ResultDataEditor ({ task, data, onChange }) {
  switch (task.data.resultType) {
    case 2: // HtmlText
      return <HtmlTextTaskResult data={data} onChange={onChange} />
    case 3: // EmployeeAssignment
      return <EmployeeAssignmentTaskResult data={data} onChange={onChange} />
    case 4: // Message
      return <MessageTaskResult data={data} targetInstitution={toIntOrNull(task.data.data)} onChange={onChange} />
    case 5: // OutputReport
      return <OutputReportTaskResult data={data} onChange={onChange} />
    case 6: // Approval
      return <ApprovalTaskResult data={data} onChange={onChange} />
    default: // ButtonsOnly
      return <div>Not Implemented</div>
  }
}

function ChangeRequestTaskResult ({ changeRequest, checkerUserId, task, hideTitle = false }) {
  const isGranted = usePermissionChecker()
  const userId = parseInt(useSelector(state => state.identity.user.profile.id))
  const canViewCHR = isGranted('CanViewChangeRequests') || checkerUserId === userId

  if (!changeRequest || !canViewCHR) return null

  return (
    <React.Fragment>
      {!hideTitle && <div className='box__title'><Resx name='Tasks.ChangeRequest' /></div>}
      <Table borderless className='table-in-white-box'>
        <thead>
          <tr>
            <th width='20px'>&nbsp;</th>
            <th><Resx name='Tasks.ChangeItemOperation' /></th>
            <th><Resx name='Tasks.ChangeItemScope' /></th>
            <th>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {changeRequest.items.map((item, index) => <ChangeItem canViewCHR={canViewCHR} key={index} item={item} task={task} />)}
        </tbody>
      </Table>
    </React.Fragment>
  )
}

function ButtonsResult ({ task }) {
  return (
    <InfoRow>
      <InfoContent><ApprovalResult task={task} /></InfoContent>
    </InfoRow>
  )
}

function HtmlTextTaskResult ({ data, onChange }) {
  const text = (data && data.text) || ''
  if (typeof onChange === 'function') {
    return (
      <InfoRow>
        <InfoContent><RichTextEditor value={text} onChange={text => onChange({ text })} /></InfoContent>
      </InfoRow>
    )
  }
  return (
    <InfoRow>
      <InfoContent>
        <div dangerouslySetInnerHTML={{ __html: text }} />
      </InfoContent>
    </InfoRow>
  )
}

function EmployeeAssignmentTaskResult ({ data, onChange }) {
  if (!data) data = {}
  if (typeof onChange === 'function') {
    return (
      <React.Fragment>
        <InfoRow>
          <InfoLabel size='sm'><Resx name='Common.Employee' /> </InfoLabel>
          <InfoContent>
            <Validator name='userId' value={data.user} required>
              <EmployeeAssignmentDropDown value={data.user} onChange={({ item }) => onChange({ user: item, userId: item ? item.id : null })} />
            </Validator>
          </InfoContent>
        </InfoRow>
        <InfoRow>
          <InfoLabel size='sm'><Resx name='Common.DueDate' /> </InfoLabel>
          <InfoContent>
            <DatePicker value={data.dueDate} onChange={value => onChange({ dueDate: value })} min={moment().add(1, 'd')} />
          </InfoContent>
        </InfoRow>
      </React.Fragment>
    )
  }
  return (
    <React.Fragment>
      <InfoRow>
        <InfoLabel size='sm'><Resx name='Common.Employee' /> </InfoLabel>
        <InfoContent>{data.user ? data.user.name : '-'}</InfoContent>
      </InfoRow>
      <InfoRow>
        <InfoLabel size='sm'><Resx name='Common.DueDate' /> </InfoLabel>
        <InfoContent>{formatDate(data.dueDate)}</InfoContent>
      </InfoRow>
    </React.Fragment>
  )
}

function MessageTaskResult ({ data, targetInstitution, onChange, showDetail }) {
  const dispatchActions = useDispatch({ openDialog })
  const { localize } = useLocalization()
  if (typeof onChange === 'function') {
    return <ComposeMessageResultData data={data} onChange={onChange} targetInstitution={targetInstitution} />
  }

  function openMessageDialog () {
    dispatchActions.openDialog({
      dialogType: dialogTypes.viewMessage,
      message: (data && data.message) || {},
      attachments: (data && data.attachments) || []
    })
  }

  return <InfoRow>
    <InfoLabel size='sm'><Resx name='Messages.MessageDraft' /> </InfoLabel>
    <InfoContent>
      {data && data.message ? data.message.subject : localize('Messages.NoSubject')}
      {showDetail && <a onClick={openMessageDialog}><Icon name='show' style={{ 'margin-left': '4px' }} /></a>}
    </InfoContent>
  </InfoRow>
}

function OutputReportTaskResult ({ data, onChange }) {
  const { localize } = useLocalization()
  const userId = useSelector(state => Number(state.identity.user.profile.sub))
  const handleChange = useCallback(({ item }) => {
    onChange({ outputReportId: item ? item.id : null, outputReport: item ? { id: item.id, name: item.name, latinName: item.latinName } : null })
  }, [onChange])
  const localizedCbiName = localize('Common.CBI')
  const dataSource = useCallback((request, opt) => {
    if (!request) request = {}
    if (!request.filter) request.filter = { logic: 'and', filters: [] }
    request.filter.filters.push({ field: 'creatorUserId', operator: 'eq', value: userId })
    request.sort = [{ field: 'creationTime', dir: 'desc' }]
    return context.api.outputReportGroupService.getTableData(localizedCbiName, false, request, opt)
  }, [localizedCbiName, userId])

  const outputReport = data && data.outputReport
  const outputReportId = data && data.outputReportId
  if (typeof onChange === 'function') {
    return (
      <React.Fragment>
        <InfoRow>
          <InfoLabel size='sm'><Resx name='Tasks.SelectOutputReport' /> </InfoLabel>
          <InfoContent>
            <DropDownList
              value={outputReport}
              dataSource={dataSource}
              onChange={handleChange} />
          </InfoContent>
        </InfoRow>
        <div>
          <Link to='/output-report-groups' className='btn btn-primary btn-sm'>{localize('Tasks.GoToOutputReports')}</Link>
        </div>
      </React.Fragment>
    )
  }
  return (
    <InfoRow>
      <InfoLabel size='sm'><Resx name='Tasks.SelectOutputReport' /> </InfoLabel>
      <InfoContent>
        {outputReport && outputReport.id
          ? <Link to={`/output-report-groups/${outputReportId}/output-reports`}><Name data={outputReport} /> <Icon name='link' /></Link>
          : '-'
        }
      </InfoContent>
    </InfoRow>
  )
}

function ApprovalTaskResult ({ data, onChange }) {
  const approved = !data || isNullOrUndefined(data.approved) ? null : data.approved

  const approve = useCallback(() => onChange({ approved: true }), [onChange])
  const reject = useCallback(() => onChange({ approved: false }), [onChange])

  const approvedInfoContent = useCallback(() => <InfoRow>
    <InfoLabel size='sm'><Resx name='Tasks.ApprovalResult' /></InfoLabel>
    <InfoContent>
      {approved === null ? '-' : null}
      {approved === true ? <Resx name={'Common.Approved'} /> : null}
      {approved === false ? <Resx name={'Common.Rejected'} /> : null}
    </InfoContent>
  </InfoRow>, [approved])

  if (typeof onChange === 'function') {
    return (
      <React.Fragment>
        {approvedInfoContent()}
        <InfoRow>
          <ButtonGroup>
            <Button size='sm' color='primary' onClick={approve}><Resx name='Common.Approve' /></Button>
            <Button size='sm' color='warning' onClick={reject}><Resx name='Common.Reject' /></Button>
          </ButtonGroup>
        </InfoRow>
      </React.Fragment>
    )
  }
  return approvedInfoContent()
}

export {
  TaskResultData,
  ResultDataEditor,
  ChangeRequestTaskResult
}



// WEBPACK FOOTER //
// ./src/internal-app/pages/tasks/taskResultData.js